import { useEffect } from "react";
import {
  OperationVariables,
  SubscribeToMoreOptions,
  SuspenseQueryHookOptions,
  TypedDocumentNode,
  useSuspenseQuery,
} from "@apollo/client";
import { getApollo } from "@songtradr-one/shared-frontend";

type NewNotification = { newNotification: boolean };

/** Suspends on a notifications query and keeps it live.
 *
 * Reads the query with `useSuspenseQuery` and subscribes to `newNotification`
 * events. When one arrives, the cached `notifications` field is evicted so the
 * query refetches fresh data.
 *
 * @see https://www.apollographql.com/docs/react/data/subscriptions#subscribing-to-updates-for-a-query
 */
export const useNotificationsFeed = <
  TData,
  TVars extends OperationVariables = OperationVariables,
>(
  query: TypedDocumentNode<TData, TVars>,
  document: SubscribeToMoreOptions<TData, TVars, NewNotification>["document"],
  variables: SuspenseQueryHookOptions<TData, TVars>["variables"],
) => {
  const offset = variables?.offset;
  const { data, subscribeToMore } = useSuspenseQuery(query, { variables });

  useEffect(() => {
    let stale = false;

    const unsubscribe = subscribeToMore<NewNotification>({
      document,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data?.newNotification) return prev;

        // At the top of the list (offset 0): evict now so it refetches fresh.
        // Once paginated / infinitely scrolled in (offset > 0): defer eviction
        // to unmount so we don't yank the items already loaded below.
        if (!offset) _evictNotificationsCache();
        else stale = true;

        return prev;
      },
    });

    return () => {
      unsubscribe();
      if (stale) _evictNotificationsCache();
    };
  }, [subscribeToMore, document, offset]);

  return data;
};

const _evictNotificationsCache = () => {
  const { client } = getApollo();
  client.cache.evict({ fieldName: "notifications" });
  client.cache.gc();
};
